import { useState } from 'react'
import { X, Loader2, AlertTriangle, ArrowRight, Package, Minus, Plus, Lock } from 'lucide-react'
import { postJson } from '../../lib/api.js'
import { relabelBranch } from '../../lib/ticket.js'
import { fmtDateTime } from '../../lib/stock.js'
import {
  STAGE_DONE, stageMeta, stageTone,
  transferQty, transferItemName, creatorCode, modifiedCode, originLevel, destLevel,
} from '../../lib/transfer.js'

// One transfer (ใบโอน) — the view/edit side popup. Who may edit is decided by the
// parent from the row's stage (ขอโอนย้าย = anyone · รับของ = Manager+ · เสร็จสิ้น =
// Admin) and passed as `canEdit`. Editable: จำนวน + หมายเหตุ. Item, เส้นทาง and date
// are read-only context — changing them makes it a different transfer, and the
// server re-derives the N sign on save (see functions/api/inventory_transfer.js).
//
// A เสร็จสิ้น edit does NOT rewrite the stock pair already posted on receipt — it's
// an admin correction of the record only, hence the warning banner.

const LABEL = 'text-[12px] font-medium uppercase tracking-wide text-slate-400 mb-1.5'
const FIELD = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[14px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-300 focus:ring-2 focus:ring-brand-100'

function fmtDate(iso) {
  if (!iso) return '—'
  const [y, m, d] = String(iso).split('-')
  if (!y) return iso
  return `${Number(d)}/${Number(m)}/${String(y).slice(2)}`
}

// postJson throws "HTTP 4xx: {json}" — pull the server's Thai `detail` back out.
function errDetail(e) {
  const m = String(e?.message || '')
  const i = m.indexOf('{')
  if (i >= 0) { try { return JSON.parse(m.slice(i))?.detail || m } catch { /* fall through */ } }
  return m
}

export default function TransferRecordDrawer({ row, stage, canEdit, onClose, onChanged }) {
  // Trust the tab's stage (the row is always opened from it); fall back to the row.
  const st = Number(stage ?? row?.stage)
  const meta = stageMeta(st)
  const isDone = st === STAGE_DONE
  const unit = row?.inventory?.unit || ''
  const origQty = transferQty(row)
  const [qty, setQty] = useState(origQty)
  const [remark, setRemark] = useState(row?.remark || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const dirty = origQty !== Number(qty) || (row?.remark || '') !== remark
  const qtyValid = Number.isInteger(Number(qty)) && Number(qty) >= 1
  const oLvl = originLevel(row), dLvl = destLevel(row)

  function bump(delta) { setQty((q) => Math.max(1, (Number(q) || 0) + delta)) }
  function onQtyInput(raw) { setQty(Math.floor(Number(String(raw).replace(/[^0-9]/g, '')) || 0)) }

  async function save() {
    setSaving(true)
    setError(null)
    try {
      // Send N as a positive magnitude — the proxy re-signs to -abs and re-checks
      // the edit permission against the STORED stage. Identity fields ride along
      // so a done (not-in-flight) admin edit can be reconstructed server-side.
      await postJson('/api/inventory_transfer', {
        inventory_transfer_id: row.id,
        inventory_id: row.inventory_id,
        branch_id: row.branch_id,
        branch2_id: row.branch2_id,
        stage: st,
        date: row.date,
        created_by: row.created_by,
        N: qty,
        remark,
      })
      onChanged?.()
      onClose?.()
    } catch (e) {
      setError(errDetail(e) || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={() => !saving && onClose?.()} />
      <div className="relative w-full max-w-[480px] bg-slate-50 shadow-2xl flex flex-col animate-ss-slide-in">

        {/* Header carries the item identity + stage + unit + id. */}
        <div className="px-5 py-3.5 bg-white border-b border-slate-200 flex items-start gap-3">
          <span className="w-9 h-9 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
            <Package size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[15.5px] font-semibold text-slate-900 leading-tight">{transferItemName(row)}</div>
            <div className="text-[11.5px] text-slate-400 mt-1 flex items-center gap-1.5 flex-wrap">
              <span className={`inline-block rounded-full border px-1.5 py-0.5 text-[10.5px] font-medium ${stageTone(row?.stage)}`}>
                {meta?.label || '—'}
              </span>
              {unit && <span>· หน่วย: {unit}</span>}
              <span>· #{row?.id}</span>
            </div>
          </div>
          <button onClick={() => !saving && onClose?.()} className="text-slate-400 hover:text-slate-600 p-1">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {!canEdit && (
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex gap-2.5 text-[13px] text-slate-600">
              <Lock size={15} className="shrink-0 mt-0.5 text-slate-400" />
              <span>
                {isDone
                  ? 'ดูได้อย่างเดียว — แก้ไขรายการที่เสร็จสิ้นแล้วได้เฉพาะแอดมิน'
                  : 'ดูได้อย่างเดียว — แก้ไขรายการนี้ได้เฉพาะผู้จัดการ'}
              </span>
            </div>
          )}
          {isDone && canEdit && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex gap-2.5 text-[12.5px] text-amber-800">
              <AlertTriangle size={15} className="shrink-0 mt-0.5" />
              <span>การแก้ไขจำนวนที่นี่จะไม่ปรับสต๊อกที่บันทึกไปแล้ว — ใช้สำหรับแก้ไขข้อมูลใบโอนให้ถูกต้องเท่านั้น</span>
            </div>
          )}

          {/* เส้นทาง — read-only (changing it makes it a different transfer). */}
          <Field label="เส้นทาง">
            <span className="inline-flex items-center gap-1.5 text-[14px] text-slate-800">
              <BranchDot color={row?.branch_color} />{relabelBranch(row?.branch_name) || `#${row?.branch_id}`}
              <ArrowRight size={13} className="text-slate-300" />
              <BranchDot color={row?.branch2_color} />{relabelBranch(row?.branch2_name) || `#${row?.branch2_id}`}
            </span>
          </Field>

          <Field label="จำนวน">
            {canEdit ? (
              <>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => bump(-1)} disabled={saving}
                    className="w-8 h-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center disabled:opacity-40">
                    <Minus size={14} />
                  </button>
                  <input value={qty} onChange={(e) => onQtyInput(e.target.value)} inputMode="numeric" disabled={saving}
                    className={`w-16 h-8 rounded-lg border text-center text-[14px] font-semibold outline-none focus:ring-2 focus:ring-brand-100 ${
                      qtyValid ? 'border-slate-200 text-slate-900 focus:border-brand-300' : 'border-amber-300 text-amber-700'}`} />
                  <button onClick={() => bump(+1)} disabled={saving}
                    className="w-8 h-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center disabled:opacity-40">
                    <Plus size={14} />
                  </button>
                  {unit && <span className="text-[12px] text-slate-400 ml-1">{unit}</span>}
                </div>
                {!qtyValid && <div className="text-[11.5px] mt-1.5 text-amber-600">จำนวนอย่างน้อย 1</div>}
              </>
            ) : (
              <div className="text-[14px] font-semibold text-slate-800">
                {origQty}{unit && <span className="text-[12px] font-normal text-slate-400 ml-1">{unit}</span>}
              </div>
            )}
          </Field>

          <Field label="คงเหลือปัจจุบัน (ต้นทาง → ปลายทาง)">
            <div className="text-[14px] text-slate-600 tabular-nums">
              {oLvl == null ? '—' : oLvl} <span className="text-slate-300">→</span> {dLvl == null ? '—' : dLvl}
            </div>
          </Field>

          <Field label="หมายเหตุ">
            {canEdit ? (
              <input value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="เพิ่มหมายเหตุ…" disabled={saving}
                className={FIELD} />
            ) : (
              <div className="text-[14px] text-slate-600">{row?.remark || '—'}</div>
            )}
          </Field>

          {/* Record info — date + who requested / received it. */}
          <section className="border-t border-slate-200 pt-4 space-y-4">
            <Field label="วันที่">
              <div className="text-[14px] text-slate-800 tabular-nums">{fmtDate(row?.date)}</div>
            </Field>
            <Field label="ผู้ทำรายการ">
              <div className="text-[14px] text-slate-700">{creatorCode(row) || '—'}</div>
            </Field>
            {isDone && (
              <Field label="ผู้รับของ">
                <div className="text-[14px] text-slate-700">
                  {modifiedCode(row) || '—'}
                  {row?.modified_at ? <span className="text-[11.5px] text-slate-400"> · {fmtDateTime(row.modified_at)}</span> : null}
                </div>
              </Field>
            )}
          </section>

          {error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 flex gap-2.5 text-[13px] text-rose-800">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {canEdit && (
          <div className="px-5 py-3.5 bg-white border-t border-slate-200 flex items-center justify-end gap-2">
            <button onClick={() => !saving && onClose?.()} disabled={saving}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-[14px] font-medium text-slate-600 hover:bg-slate-50">
              ปิด
            </button>
            <button onClick={save} disabled={saving || !dirty || !qtyValid}
              className="rounded-xl bg-brand-600 px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-brand-700 disabled:opacity-40 flex items-center gap-2">
              {saving && <Loader2 size={15} className="animate-spin" />}บันทึก
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return <div><div className={LABEL}>{label}</div>{children}</div>
}

function BranchDot({ color }) {
  if (!color) return null
  return <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
}
